"use client";

import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useSession } from 'next-auth/react';

// Define the shape of an item in the cart
interface CartItem {
  name: string;
  newPrice: number;
  type: 'Service' | 'Bundle' | 'Training';
  quantity: number;
  isQuantifiable?: boolean;
  oneTimeFee?: number;
}

// Define the shape of the context data
interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  subtotal: number;
  addToCart: (item: CartItem, associatedServices?: string[]) => void;
  removeFromCart: (itemName: string) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  servicesIncludedInBundle: string[];
  addBundleAndRemoveServices: (bundleToAdd: CartItem, servicesToRemove: string[], bundleServices: string[]) => void;
  updateQuantity: (itemName: string, quantity: number) => void;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Create the provider component
export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { data: session } = useSession();

  // Create dynamic keys for localStorage based on the user's email.
  const cartItemsKey = session ? `cartItems_${session.user?.email}` : 'cartItems_guest';
  const servicesKey = session ? `servicesIncludedInBundle_${session.user?.email}` : 'servicesIncludedInBundle_guest';

  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedItems = localStorage.getItem(cartItemsKey);
        return storedItems ? JSON.parse(storedItems) : [];
      } catch (error) {
        console.error("Failed to parse cart items from localStorage", error);
        return [];
      }
    }
    return [];
  });

  const [servicesIncludedInBundle, setServicesIncludedInBundle] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedServices = localStorage.getItem(servicesKey);
        return storedServices ? JSON.parse(storedServices) : [];
      } catch (error) {
        console.error("Failed to parse bundle services from localStorage", error);
        return [];
      }
    }
    return [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedItems = localStorage.getItem(cartItemsKey);
      setCartItems(storedItems ? JSON.parse(storedItems) : []);

      const storedServices = localStorage.getItem(servicesKey);
      setServicesIncludedInBundle(storedServices ? JSON.parse(storedServices) : []);
    }
  }, [session, cartItemsKey, servicesKey]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(cartItemsKey, JSON.stringify(cartItems));
    }
  }, [cartItems, cartItemsKey]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(servicesKey, JSON.stringify(servicesIncludedInBundle));
    }
  }, [servicesIncludedInBundle, servicesKey]);

  // Updated addToCart Logic
  const addToCart = (item: CartItem, associatedServices: string[] = []) => {
    setCartItems(prevItems => {
      // Check if item is Training OR a Quantifiable Service
      const isStackable = item.type === 'Training' || (item.type === 'Service' && item.isQuantifiable);

      if (isStackable) {
        const existingItem = prevItems.find(i => i.name === item.name);
        if (existingItem) {
          // Update quantity
          return prevItems.map(i =>
            i.name === item.name ? { ...i, quantity: (i.quantity || 0) + 1 } : i
          );
        }
      }

      const existingService = prevItems.find(i => i.name === item.name && !isStackable);
      if (existingService) return prevItems; // Return existing state if duplicate subscription service

      return [...prevItems, { ...item, quantity: 1 }];
    });

    if (item.type === 'Bundle') {
      setServicesIncludedInBundle(associatedServices);
    }
  };

  const clearCart = () => {
    setCartItems([]);
    setServicesIncludedInBundle([]);
  };

  const removeFromCart = (itemName: string) => {
    const itemToRemove = cartItems.find(item => item.name === itemName);
    if (itemToRemove && itemToRemove.type === 'Bundle') {
      setServicesIncludedInBundle([]);
    }
    setCartItems(prevItems => prevItems.filter(item => item.name !== itemName));
  };

  // Update quantity for an item
  const updateQuantity = (itemName: string, quantity: number) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.name === itemName ? { ...item, quantity: Math.max(1, quantity) } : item // Ensure quantity is at least 1
      )
    );
  };

  const addBundleAndRemoveServices = (
    bundleToAdd: CartItem,
    servicesToRemove: string[],
    bundleServices: string[]
  ) => {
    const updatedCart = cartItems.filter(item => !servicesToRemove.includes(item.name));
    updatedCart.push({ ...bundleToAdd, quantity: 1 });
    setCartItems(updatedCart);
    setServicesIncludedInBundle(bundleServices);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
  const subtotal = cartItems.reduce((sum, item) => {
    const itemTotal = Number(item.newPrice || 0) * (item.quantity || 1);
    const feeTotal = Number(item.oneTimeFee || 0); // One-time fee is added once per item entry
    return sum + itemTotal + feeTotal;
  }, 0);

  const value = {
    cartItems,
    cartCount,
    subtotal,
    addToCart,
    removeFromCart,
    clearCart,
    isCartOpen,
    openCart,
    closeCart,
    servicesIncludedInBundle,
    addBundleAndRemoveServices,
    updateQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};